// localStorage helpers for saved calculations and favorites
import { getCachedEntitlement } from "./entitlement";
import { isProActive, FeatureLockedError } from "./featureAccess";
import { scoped, currentScopeSuffix } from "./accountScope";

const SAVED_KEY = "rsc.saved.v1";
const FAV_KEY = "rsc.favs.v1";
const RECENT_KEY = "rsc.recent.v1";
const SETTINGS_KEY = "rsc.settings.v1";

const safeParse = (s, fallback) => {
  try { return JSON.parse(s) ?? fallback; } catch { return fallback; }
};

// Phase A Pro guard for mutating localStorage operations. Read paths stay
// public so Preview users can see their pre-existing data; writes/deletes
// throw FeatureLockedError so global error handling surfaces the upgrade UI.
function _requireProForWrite(action) {
  if (!isProActive(getCachedEntitlement())) {
    throw new FeatureLockedError(
      `${action} is available with RigSentinel Pro.`,
      { feature: "storage.write" },
    );
  }
}

export function getSaved() {
  return safeParse(scoped.getItem(SAVED_KEY), []);
}
export function saveCalculation(entry) {
  _requireProForWrite("Saving a calculation");
  const list = getSaved();
  const item = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date().toISOString(),
    ...entry,
  };
  list.unshift(item);
  scoped.setItem(SAVED_KEY, JSON.stringify(list.slice(0, 200)));
  return item;
}
export function deleteSaved(id) {
  _requireProForWrite("Editing saved calculations");
  const list = getSaved().filter((x) => x.id !== id);
  scoped.setItem(SAVED_KEY, JSON.stringify(list));
}
export function clearSaved() {
  _requireProForWrite("Clearing saved calculations");
  scoped.removeItem(SAVED_KEY);
}

export function getFavorites() { return safeParse(scoped.getItem(FAV_KEY), []); }
export function isFavorite(calcId) { return getFavorites().includes(calcId); }
export function toggleFavorite(calcId) {
  _requireProForWrite("Managing favorites");
  const list = getFavorites();
  const idx = list.indexOf(calcId);
  if (idx >= 0) list.splice(idx, 1);
  else list.unshift(calcId);
  scoped.setItem(FAV_KEY, JSON.stringify(list));
  return list.includes(calcId);
}

export function getRecent() { return safeParse(scoped.getItem(RECENT_KEY), []); }
export function pushRecent(calcId) {
  const list = getRecent().filter((x) => x !== calcId);
  list.unshift(calcId);
  scoped.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 10)));
}
export function clearRecent() { scoped.removeItem(RECENT_KEY); }

// =========================================================================
// Phase R2c — Default Planning Factors (safety thresholds).
//
// Stored under the existing `rsc.settings.v1` key in a `safetyThresholds`
// sub-object. Factory defaults are seeded from the R2a merged library
// (`getSafetyThresholds()`) on first read. These values are USER-EDITABLE
// PLANNING AIDS — they are never applied to a calculation output in R2c.
// =========================================================================
import { loadMergedLibrary as _loadMergedLib } from "@/lib/reference/normalize";

function _defaultPlanningFactors() {
  try { return { ..._loadMergedLib().safety_thresholds.default }; } catch { return {}; }
}

export function getPlanningFactors() {
  const s = safeParse(scoped.getItem(SETTINGS_KEY), {});
  return { ..._defaultPlanningFactors(), ...(s.safetyThresholds || {}) };
}

export function setPlanningFactor(key, value) {
  const s = safeParse(scoped.getItem(SETTINGS_KEY), {});
  const next = { ...(s.safetyThresholds || {}), [key]: value };
  scoped.setItem(SETTINGS_KEY, JSON.stringify({ ...s, safetyThresholds: next }));
  return next;
}

export function resetPlanningFactor(key) {
  const s = safeParse(scoped.getItem(SETTINGS_KEY), {});
  if (!s.safetyThresholds) return getPlanningFactors();
  const next = { ...s.safetyThresholds };
  delete next[key];
  scoped.setItem(SETTINGS_KEY, JSON.stringify({ ...s, safetyThresholds: next }));
  return getPlanningFactors();
}

export function resetAllPlanningFactors() {
  const s = safeParse(scoped.getItem(SETTINGS_KEY), {});
  const { safetyThresholds, ...rest } = s; // eslint-disable-line no-unused-vars
  scoped.setItem(SETTINGS_KEY, JSON.stringify(rest));
  return getPlanningFactors();
}

// =========================================================================
// RCE-4.5 — Engineering Defaults.
//
// Operating defaults that flow into Kill Sheets (and future operational
// reports) via the RCE Context Engine. Stored as a `engineeringDefaults`
// sub-object on `rsc.settings.v1`. Default values match the legacy Kill
// Sheet schema defaults so existing user data behaves identically.
// =========================================================================
const ENG_DEFAULTS_FACTORY = Object.freeze({
  safetyMarginPpg:          "0.3",
  scheduleIntervalStrokes:  "100",
  wellControlStandard:      "",
});

export function getEngineeringDefaults() {
  const s = safeParse(scoped.getItem(SETTINGS_KEY), {});
  return { ...ENG_DEFAULTS_FACTORY, ...(s.engineeringDefaults || {}) };
}

export function setEngineeringDefault(key, value) {
  if (!(key in ENG_DEFAULTS_FACTORY))
    throw new Error(`Unknown engineering default: ${key}`);
  const s = safeParse(scoped.getItem(SETTINGS_KEY), {});
  const next = { ...(s.engineeringDefaults || {}), [key]: value };
  scoped.setItem(SETTINGS_KEY, JSON.stringify({ ...s, engineeringDefaults: next }));
  return getEngineeringDefaults();
}

export const ENGINEERING_DEFAULT_KEYS = Object.freeze(Object.keys(ENG_DEFAULTS_FACTORY));

// =========================================================================
// Local-storage quota helpers (Phase 1 offline hardening). These are read-only
// utilities — they never mutate storage and are NOT gated by Pro because the
// Storage section in Settings must work for every user.
// =========================================================================
const STORAGE_SOFT_LIMIT_BYTES = 4 * 1024 * 1024; // 4 MB warning threshold
const STORAGE_HARD_LIMIT_BYTES = 5 * 1024 * 1024; // ~browser localStorage cap

export function getLocalStorageUsageBytes() {
  try {
    let total = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k == null) continue;
      const v = localStorage.getItem(k) || "";
      // UTF-16 in JS strings ≈ 2 bytes per char; storage spec uses code units.
      total += k.length + v.length;
    }
    return total * 2;
  } catch {
    return 0;
  }
}

export function getLocalStorageUsagePct() {
  const used = getLocalStorageUsageBytes();
  return Math.min(100, Math.round((used / STORAGE_HARD_LIMIT_BYTES) * 100));
}

export function getStorageLimits() {
  return {
    softLimitBytes: STORAGE_SOFT_LIMIT_BYTES,
    hardLimitBytes: STORAGE_HARD_LIMIT_BYTES,
  };
}

// Returns { warn: bool, message: string } if the user has crossed the 4 MB
// soft limit. The Settings page renders this synchronously; write paths call
// it once after each save and surface a toast (gated by an in-memory flag so
// we don't spam every report save).
export function checkStorageWarning() {
  const used = getLocalStorageUsageBytes();
  if (used >= STORAGE_SOFT_LIMIT_BYTES) {
    return {
      warn: true,
      message: "Local storage nearly full. Export or delete old reports.",
      usedBytes: used,
    };
  }
  return { warn: false, message: "", usedBytes: used };
}

// Exports every RigSentinel-owned localStorage key BELONGING TO THE
// CURRENTLY SIGNED-IN ACCOUNT as a single JSON object suitable for
// download. Read-only; never deletes or modifies data. Filtered by the
// current scope suffix so a shared device never exports another account's
// data. The "Export All Reports" button in Settings calls this.
export function exportAllLocalData() {
  const PREFIX = "rsc.";
  const suffix = currentScopeSuffix();
  const payload = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k == null) continue;
      if (!k.startsWith(PREFIX)) continue;
      if (!suffix || !k.endsWith(suffix)) continue;
      const raw = localStorage.getItem(k);
      try { payload[k] = JSON.parse(raw); } catch { payload[k] = raw; }
    }
  } catch { /* noop */ }
  return {
    app: "RigSentinel",
    schema: "rsc.backup.v1",
    exportedAt: new Date().toISOString(),
    appVersion: (typeof window !== "undefined" && window.__RSC_VERSION__) || "1.0.0",
    data: payload,
  };
}

export function getSettings() {
  return safeParse(scoped.getItem(SETTINGS_KEY), {
    units: "imperial",
    showSafetyOnLaunch: true,
    defaultWell: "",
    defaultRig: "",
  });
}
export function setSettings(patch) {
  const next = { ...getSettings(), ...patch };
  scoped.setItem(SETTINGS_KEY, JSON.stringify(next));
  return next;
}

// =========================================================================
// Subsea Well Control Profile — minimal local-only profile so the Subsea tab
// has something to open/edit. Used by the Well Control category page to
// switch its button label between "Add" and "Open" Subsea Well Control Profile.
// =========================================================================
const SUBSEA_PROFILE_KEY = "rsc.subseaProfile.v1";

// Canonical empty profile shape — order preserved here is the order the UI
// renders fields in. All fields are optional strings/numbers stored as strings
// so the UI can keep typed values verbatim.
export const SUBSEA_PROFILE_FIELDS = [
  { key: "rigName",                label: "Rig name",                              group: "header"    },
  { key: "wellName",               label: "Well name",                             group: "header"    },
  { key: "waterDepth",             label: "Water depth",                unit: "ft", group: "depths"    },
  { key: "rkbToSeaLevel",          label: "RKB to sea level",           unit: "ft", group: "depths"    },
  { key: "seaLevelToMudline",      label: "Sea level to mudline",       unit: "ft", group: "depths"    },
  { key: "rkbToMudline",           label: "RKB to mudline",             unit: "ft", group: "depths"    },
  { key: "bopStackDepth",          label: "BOP stack depth",            unit: "ft", group: "depths"    },
  { key: "wellheadDepth",          label: "Subsea wellhead depth",      unit: "ft", group: "depths"    },
  { key: "riserId",                label: "Marine riser ID",            unit: "in", group: "riser"     },
  { key: "riserLength",            label: "Marine riser length",        unit: "ft", group: "riser"     },
  { key: "riserCapacity",          label: "Riser capacity",         unit: "bbl/ft", group: "riser"     },
  { key: "chokeLineId",            label: "Choke line ID",              unit: "in", group: "choke"     },
  { key: "chokeLineLength",        label: "Choke line length",          unit: "ft", group: "choke"     },
  { key: "chokeLineCapacity",      label: "Choke line capacity",    unit: "bbl/ft", group: "choke"     },
  { key: "chokeLineFriction",      label: "Choke line friction (at selected kill rate)", unit: "psi",  group: "choke" },
  { key: "chokeFrictionTestRate",  label: "Choke line friction test rate",            unit: "spm",     group: "choke" },
  { key: "chokeFrictionTestDate",  label: "Choke line friction test date",            unit: "",        group: "choke" },
  { key: "killLineId",             label: "Kill line ID",               unit: "in", group: "kill"      },
  { key: "killLineLength",         label: "Kill line length",           unit: "ft", group: "kill"      },
  { key: "killLineCapacity",       label: "Kill line capacity",     unit: "bbl/ft", group: "kill"      },
  { key: "killLineFriction",       label: "Kill line friction (at selected kill rate)", unit: "psi",   group: "kill" },
  { key: "killFrictionTestRate",   label: "Kill line friction test rate",             unit: "spm",     group: "kill" },
  { key: "killFrictionTestDate",   label: "Kill line friction test date",             unit: "",        group: "kill" },
  { key: "scrPressure",            label: "Slow circulating rate pressure",           unit: "psi",     group: "pump" },
  { key: "killRate",               label: "Kill rate",                                unit: "spm",     group: "pump" },
  { key: "pumpOutput",             label: "Pump output",                          unit: "bbl/stk",     group: "pump" },
  { key: "currentMW",              label: "Current mud weight",                      unit: "ppg",      group: "fluids" },
  { key: "riserMarginMW",          label: "Riser margin mud weight",                 unit: "ppg",      group: "fluids" },
  { key: "casingShoeTvd",          label: "Casing shoe TVD",                          unit: "ft",      group: "wellbore" },
  { key: "openHoleTvd",            label: "Open hole TVD",                            unit: "ft",      group: "wellbore" },
  { key: "bitDepthMd",             label: "Bit depth MD",                             unit: "ft",      group: "wellbore" },
  { key: "bitDepthTvd",            label: "Bit depth TVD",                            unit: "ft",      group: "wellbore" },
  { key: "holeDepthMd",            label: "Hole depth MD",                            unit: "ft",      group: "wellbore" },
  { key: "holeDepthTvd",           label: "Hole depth TVD",                           unit: "ft",      group: "wellbore" },
  { key: "chokeManifoldNotes",     label: "Choke manifold notes",                     unit: "",        group: "notes", multiline: true },
  { key: "stackConfigNotes",       label: "Subsea stack configuration notes",         unit: "",        group: "notes", multiline: true },
  { key: "podControlNotes",        label: "Pod / control system notes",               unit: "",        group: "notes", multiline: true },
  { key: "rovNotes",               label: "ROV intervention notes",                   unit: "",        group: "notes", multiline: true },
  { key: "procedureRef",           label: "Company procedure reference",              unit: "",        group: "notes" },
  { key: "userNotes",              label: "User notes",                               unit: "",        group: "notes", multiline: true },
];

export const SUBSEA_PROFILE_GROUPS = [
  { id: "header",   title: "Identification" },
  { id: "depths",   title: "Depths" },
  { id: "riser",    title: "Marine Riser" },
  { id: "choke",    title: "Choke Line" },
  { id: "kill",     title: "Kill Line" },
  { id: "pump",     title: "Pump / Kill Rate" },
  { id: "fluids",   title: "Fluids" },
  { id: "wellbore", title: "Wellbore" },
  { id: "notes",    title: "Notes & References" },
];

function _emptyProfileObject() {
  const o = {};
  SUBSEA_PROFILE_FIELDS.forEach((f) => { o[f.key] = ""; });
  return o;
}

export function getSubseaProfile() {
  const p = safeParse(scoped.getItem(SUBSEA_PROFILE_KEY), null);
  return p && typeof p === "object" ? p : null;
}
export function saveSubseaProfile(profile) {
  const next = {
    ..._emptyProfileObject(),
    ...(profile || {}),
    updatedAt: new Date().toISOString(),
  };
  scoped.setItem(SUBSEA_PROFILE_KEY, JSON.stringify(next));
  return next;
}
export function clearSubseaProfile() {
  scoped.removeItem(SUBSEA_PROFILE_KEY);
}
